import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Activity, Shield, Clock, Search, Plus, Pencil, Trash2, Eye, KeyRound, UserPlus, LogIn, LogOut } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

var entityTypes = ["all", "property", "unit", "asset", "document", "contract", "service", "rental", "key", "user"];

var actionIcons = {
  create: Plus,
  update: Pencil,
  delete: Trash2,
  view: Eye,
  handover: KeyRound,
  assign_viewer: UserPlus,
  remove_viewer: UserPlus,
  login: LogIn,
  logout: LogOut,
};

var actionColors = {
  create: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  update: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  delete: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  view: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400",
  handover: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
  assign_viewer: "bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400",
  remove_viewer: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
  login: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
  logout: "bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400",
  register: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400",
};

export default function ActivityLogs() {
  var { user, token } = useAuth();
  var { t } = useTranslation();
  var [logs, setLogs] = useState([]);
  var [loading, setLoading] = useState(true);
  var [filterType, setFilterType] = useState("all");
  var [search, setSearch] = useState("");

  // All users except viewers can see activity logs (filtered by their hierarchy)
  var canViewLogs = user?.role === "admin" || user?.role === "realestate_admin" || user?.role === "realestate_manager";

  useEffect(function() {
    if (canViewLogs && token) fetchLogs();
    else setLoading(false);
  }, [canViewLogs, token]);

  useEffect(function() {
    if (canViewLogs && token) {
      setLoading(true);
      fetchLogs();
    }
  }, [filterType]);

  async function fetchLogs() {
    try {
      var url = API_URL + "/api/activity-logs?limit=200";
      if (filterType !== "all") {
        url += "&entity_type=" + filterType;
      }
      var response = await fetch(url, {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (response.ok) {
        setLogs(await response.json());
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      setLoading(false);
    }
  }

  function formatTimestamp(timestamp) {
    if (!timestamp) return "-";
    var date = new Date(timestamp);
    return date.toLocaleString();
  }

  function getActionBadge(action) {
    var IconComponent = actionIcons[action] || Activity;
    var colorClass = actionColors[action] || "bg-gray-100 text-gray-800";
    var actionKey = "activity.actions." + action;
    return (
      <Badge className={colorClass + " flex items-center gap-1 w-fit"}>
        <IconComponent className="w-3 h-3" />
        <span>{t(actionKey)}</span>
      </Badge>
    );
  }

  function getFilteredLogs() {
    var result = logs;
    if (search) {
      var searchLower = search.toLowerCase();
      var filtered = [];
      for (var i = 0; i < result.length; i++) {
        var log = result[i];
        var matchesUser = log.user_name && log.user_name.toLowerCase().indexOf(searchLower) >= 0;
        var matchesEntity = log.entity_name && log.entity_name.toLowerCase().indexOf(searchLower) >= 0;
        var matchesType = log.entity_type && log.entity_type.toLowerCase().indexOf(searchLower) >= 0;
        if (matchesUser || matchesEntity || matchesType) {
          filtered.push(log);
        }
      }
      result = filtered;
    }
    return result;
  }

  function renderEntityFilterOptions() {
    var items = [];
    for (var i = 0; i < entityTypes.length; i++) {
      var type = entityTypes[i];
      var label = type === "all" ? t("activity.allTypes") : type.charAt(0).toUpperCase() + type.slice(1);
      items.push(<SelectItem key={type} value={type}>{label}</SelectItem>);
    }
    return items;
  }

  function renderDetailsCell(log) {
    if (!log.details || Object.keys(log.details).length === 0) return "-";
    
    var details = log.details;
    var parts = [];
    
    if (details.viewer_id) {
      parts.push("Viewer: " + details.viewer_id.substring(0, 12) + "...");
    }
    if (details.from && details.to) {
      parts.push(details.from + " → " + details.to);
    }
    if (details.role) {
      parts.push("Role: " + details.role);
    }
    
    if (parts.length > 0) {
      return parts.join(", ");
    }
    
    var jsonStr = JSON.stringify(details);
    if (jsonStr.length > 40) {
      return jsonStr.substring(0, 40) + "...";
    }
    return jsonStr;
  }

  function renderLogRows() {
    var filteredLogs = getFilteredLogs();
    var rows = [];
    for (var i = 0; i < filteredLogs.length; i++) {
      var log = filteredLogs[i];
      rows.push(
        <TableRow key={log.log_id || i} data-testid={"activity-log-" + (log.log_id || i)}>
          <TableCell>
            <div className="flex items-center gap-2">
              <Avatar className="w-7 h-7">
                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                  {log.user_name ? log.user_name.charAt(0).toUpperCase() : "?"}
                </AvatarFallback>
              </Avatar>
              <span className="font-medium text-sm">{log.user_name || "-"}</span>
            </div>
          </TableCell>
          <TableCell>{getActionBadge(log.action)}</TableCell>
          <TableCell>
            <Badge variant="outline" className="text-xs">{log.entity_type}</Badge>
          </TableCell>
          <TableCell className="max-w-[180px] truncate text-sm">{log.entity_name || "-"}</TableCell>
          <TableCell className="max-w-[180px] truncate text-xs text-muted-foreground">
            {renderDetailsCell(log)}
          </TableCell>
          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatTimestamp(log.timestamp)}
            </div>
          </TableCell>
        </TableRow>
      );
    }
    return rows;
  }

  if (!canViewLogs) {
    return (
      <div className="space-y-6" data-testid="activity-logs-page">
        <h1 className="text-3xl font-bold tracking-tight">{t("activity.title")}</h1>
        <Card className="bento-card">
          <CardContent className="py-12 text-center text-muted-foreground">
            <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{t("users.noPermission")}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  var filteredLogs = getFilteredLogs();

  return (
    <div className="space-y-6" data-testid="activity-logs-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("activity.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("activity.subtitle")}</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t("common.search")}
            value={search}
            onChange={function(e) { setSearch(e.target.value); }}
            className="pl-10"
            data-testid="activity-search-input"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-48" data-testid="activity-filter-type">
            <SelectValue placeholder={t("activity.filterByType")} />
          </SelectTrigger>
          <SelectContent>{renderEntityFilterOptions()}</SelectContent>
        </Select>
      </div>

      {filteredLogs.length > 0 ? (
        <Card className="bento-card overflow-hidden">
          <ScrollArea className="h-[600px]">
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[140px]">User</TableHead>
                  <TableHead className="w-[110px]">Action</TableHead>
                  <TableHead className="w-[90px]">Type</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead className="w-[160px]">Timestamp</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>{renderLogRows()}</TableBody>
            </Table>
          </ScrollArea>
        </Card>
      ) : (
        <Card className="bento-card">
          <CardContent className="py-12 text-center text-muted-foreground">
            <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{t("activity.noLogs")}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
